/* Solo configuration: Size Chart sizes + the actual Shopify product.
   Custom Size supplies fit only. Standard orders retain the native form;
   saved Custom Size uses the existing garment/service pair. */
(function () {
  'use strict';

  var GENDER_TEXT = {
    WOMEN: 'Women',
    MEN: 'Men',
    GIRLS: 'Girls',
    BOYS: 'Boys'
  };

  var customisationByRoot = new WeakMap();
  var submittingByRoot = new WeakMap();

  function getProductForm(root) {
    var formId = root.getAttribute('data-solo-product-form-id');
    return formId ? document.getElementById(formId) : null;
  }

  function getAddToCartButton(root) {
    var form = getProductForm(root);
    return form ? form.querySelector('[name="add"]') : null;
  }

  function getCustomisation(root) {
    return customisationByRoot.get(root) || null;
  }

  function setCustomisation(root, draft) {
    if (draft) customisationByRoot.set(root, draft);
    else customisationByRoot.delete(root);
  }

  function isCustomised(root) {
    return !!getCustomisation(root);
  }

  function getState(root) {
    var sizeSelect = root.querySelector('[data-solo-size-select]');
    return {
      size: (sizeSelect && sizeSelect.value) || '',
      customisation: getCustomisation(root)
    };
  }

  function isStateComplete(state) {
    return !!(state.customisation || state.size);
  }

  function getCartError(root) {
    return root.querySelector('[data-solo-cart-error]');
  }

  function setCartError(root, message) {
    var el = getCartError(root);
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  /* ---------------------------------------------------------------
     DOM sync -- pure render, no side effects, safe to call as often
     as needed (mirrors the old file's own refresh() discipline).
  ------------------------------------------------------------------ */
  function updateCustomisationVisibility(root) {
    var customised = isCustomised(root);
    var sizeSelect = root.querySelector('[data-solo-size-select]');
    var ctaEl = root.querySelector('[data-solo-customise-row] > [data-solo-customise-cta]');
    var panelEl = root.querySelector('[data-solo-customised-panel]');
    var detailEl = root.querySelector('[data-solo-customised-detail]');

    if (sizeSelect && window.PTSizeGuide) {
      var gender = (root.dataset.soloGender || '').toUpperCase();
      Array.from(sizeSelect.options).forEach(function (option) {
        if (option.value) option.textContent = window.PTSizeGuide.optionLabel(gender, option.value);
      });
      window.PTSizeGuide.snapshot(root.querySelector('.solo-options__section--size'), gender, sizeSelect.value, customised);
    }
    if (sizeSelect) sizeSelect.disabled = customised;
    if (ctaEl) ctaEl.hidden = customised;
    if (panelEl) panelEl.hidden = !customised;

    if (customised && detailEl) {
      var custInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
      detailEl.textContent = (custInfo && custInfo.priceFormatted)
        ? ('Custom Size & Fit · ' + custInfo.priceFormatted)
        : 'Custom Size & Fit';
    }
  }

  function updatePurchaseSummary(root) {
    var customised = isCustomised(root);
    var standardBlock = root.querySelector('[data-solo-purchase-standard]');
    var customisedBlock = root.querySelector('[data-solo-purchase-customised]');
    if (standardBlock) standardBlock.hidden = customised;
    if (customisedBlock) customisedBlock.hidden = !customised;
  }

  function updateAddToCart(root, complete) {
    var button = getAddToCartButton(root);
    if (!button) return;
    if (complete && !submittingByRoot.get(root)) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', 'true');
    }
  }

  function updateAtcGuidance(root, state, complete) {
    var button = getAddToCartButton(root);
    if (!button) return;
    var guidance = button.parentNode.querySelector('[data-solo-atc-guidance]');
    if (!guidance) {
      guidance = document.createElement('p');
      guidance.className = 'solo-atc-guidance';
      guidance.setAttribute('data-solo-atc-guidance', '');
      guidance.setAttribute('aria-live', 'polite');
      button.parentNode.insertBefore(guidance, button);
    }
    guidance.classList.toggle('solo-atc-guidance--ready', complete);
    guidance.textContent = complete
      ? 'Your selection is ready'
      : 'Select your size to continue';
  }

  // Gates the whole Customise CTA row on real availability -- same
  // check Fabric performs before ever showing its own CTA (never a
  // dead-end entry point). Solo's Gender can never change at runtime,
  // so this only needs to run once, but it still calls the real API
  // rather than assuming true, for consistency with the established
  // Fabric/Matching pattern.
  function updateCustomiseCtaAvailability(root) {
    var rowEl = root.querySelector('[data-solo-customise-row]');
    if (!rowEl) return;

    var gender = (root.getAttribute('data-solo-gender') || '').toUpperCase();
    var custInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : { available: false };
    var genderReady = !!(gender && window.PTCustomisation && window.PTCustomisation.isGenderReady(gender));
    var available = !!(custInfo && custInfo.available) && genderReady;

    rowEl.hidden = !available;

    var ctaPriceEl = root.querySelector('[data-solo-customise-cta-price]');
    if (ctaPriceEl && custInfo && custInfo.priceFormatted) {
      ctaPriceEl.textContent = 'Custom size & fit +' + custInfo.priceFormatted;
    }
  }

  function refresh(root) {
    var state = getState(root);
    var complete = isStateComplete(state);
    updateCustomisationVisibility(root);
    updatePurchaseSummary(root);
    updateAddToCart(root, complete);
    updateAtcGuidance(root, state, complete);
  }

  /* ---------------------------------------------------------------
     Customised Add to Cart -- one multi-item fetch(theme.routes.
     cartAdd), mirroring assets/fabric-product-options.js's own
     Customised branch structurally: same two-line shape (garment +
     Customisation service), same _customisation_id correlation
     pattern (freshly generated at submission time via the shared
     PTCustomisation.generateId() helper, never reused from a stale
     draft), same success/error handling conventions. No _set_id, no
     _person, no _relationship -- Solo never groups cart lines.
  ------------------------------------------------------------------ */
  function buildCustomisationProperties(root, draft, customisationId) {
    var gender = (root.getAttribute('data-solo-gender') || '').toUpperCase();
    var genderText = GENDER_TEXT[gender] || gender;

    // Unconditional fields, matching Fabric's own structured-properties
    // convention for its Customisation line (Profile/Gender/
    // Measurements Later are always present there too). Profile falls
    // back to the resolved Gender text -- the same fallback the shared
    // popup's own review summary already uses for its "For" row --
    // rather than a Fabric-style "Set N" label, which has no meaning
    // for a single-garment purchase.
    var props = {
      Profile: draft.profileName || genderText,
      Gender: genderText,
      'Measurements Later': draft.measurementsLater ? 'Yes' : 'No',
      _customisation_id: customisationId
    };

    // Optional fields -- omitted entirely when empty, never sent blank.
    if (draft.topSize) props['Top Size'] = draft.topSize;
    if (draft.bottomSize) props['Bottom Size'] = draft.bottomSize;
    if (draft.chest) props.Chest = draft.chest + ' in';
    if (draft.waist) props.Waist = draft.waist + ' in';
    if (draft.hips) props.Hips = draft.hips + ' in';
    if (draft.height) props.Height = draft.height;
    if (draft.notes) props.Notes = draft.notes;

    return props;
  }

  function submitCustomised(root, button) {
    if (submittingByRoot.get(root)) return;

    var draft = getCustomisation(root);
    if (!draft) return;

    // The Customisation product is a separate, merchant-managed Theme
    // Setting that could in principle change between page load and
    // this click (same defensive check Fabric performs before its own
    // Customised submission) -- fail safely rather than submit a line
    // with no real variant id.
    var custInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
    if (!custInfo || !custInfo.available) {
      setCartError(root, 'The Customisation service is temporarily unavailable. Please try again shortly, or use standard options instead.');
      return;
    }

    var form = getProductForm(root);
    var variantInput = form && form.querySelector('[name="id"]');
    var variantId = variantInput && variantInput.value;
    if (!variantId) {
      setCartError(root, 'Something went wrong preparing your order. Please refresh the page and try again.');
      return;
    }

    setCartError(root, null);
    submittingByRoot.set(root, true);
    if (button) button.setAttribute('aria-disabled', 'true');

    var customisationId = window.PTCustomisation.generateId();
    var garmentProperties = {
      Customisation: 'Yes — see the linked Customisation line',
      _customisation_id: customisationId
    };
    var customisationProperties = buildCustomisationProperties(root, draft, customisationId);

    var items = [
      { id: Number(variantId), quantity: 1, properties: garmentProperties },
      { id: custInfo.variantId, quantity: 1, properties: customisationProperties }
    ];

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json()
          .catch(function () { return null; })
          .then(function (json) { return { ok: response.ok, json: json }; });
      })
      .then(function (result) {
        submittingByRoot.set(root, false);
        if (button) button.removeAttribute('aria-disabled');

        if (!result.ok) {
          var errorMessage = 'We could not add this to your cart. Please try again.';
          if (result.json) {
            if (typeof result.json.description === 'string' && result.json.description) {
              errorMessage = result.json.description;
            } else if (typeof result.json.message === 'string' && result.json.message) {
              errorMessage = result.json.message;
            }
          }
          throw new Error(errorMessage);
        }
        if (!result.json || !Array.isArray(result.json.items)) {
          throw new Error('We could not add this to your cart. Please try again.');
        }

        // Native Symmetry 8.3.2 mechanism -- the same two events
        // Fabric's own Customised submission already dispatches to
        // refresh the header cart count and cart-drawer, then open
        // the drawer. No manual DOM/count updates here.
        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          cancelable: false
        }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          cancelable: false,
          detail: { opener: button }
        }));
        root.dispatchEvent(new CustomEvent('on:solo:cart:add', { bubbles: true, detail: { variantId: Number(variantId) } }));
      })
      .catch(function (error) {
        submittingByRoot.set(root, false);
        if (button) button.removeAttribute('aria-disabled');
        setCartError(root, error && error.message ? error.message : 'We could not add this to your cart. Please try again.');
        // eslint-disable-next-line no-console
        console.log(error);
      });
  }

  /**
   * Meta Pixel funnel events (ProductEngaged / ProductConfigured).
   * See assets/pdp-funnel-tracking.js for the shared fbq wrapper.
   * Local to this one root/init() call -- resets per page load, never
   * persists across navigations. Additive observers only: never touch
   * PDP state, validation, the cart, or the Add to Cart button.
   */
  function trackingParams(root, extra) {
    var ds = root.dataset || {};
    var params = {
      content_id: ds.soloProductId || undefined,
      content_name: ds.soloProductTitle || undefined,
      product_type: ds.soloProductType || undefined,
      currency: ds.soloCurrency || undefined
    };
    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) params[key] = extra[key];
      }
    }
    return params;
  }

  function configuredValue(root, state) {
    var priceCents = Number(root.getAttribute('data-solo-price-cents')) || 0;
    if (state.customisation) {
      var custInfo = window.PTCustomisation ? window.PTCustomisation.getProductInfo() : null;
      if (custInfo && typeof custInfo.priceCents === 'number') priceCents += custInfo.priceCents;
    }
    return priceCents ? (priceCents / 100) : undefined;
  }

  function init(root) {
    if (root.dataset.soloInitialized) return;
    root.dataset.soloInitialized = 'true';
    var engagedFired = false;
    var configuredFired = false;

    // Fires once per page visit, from whichever of two entry points
    // happens first: touching Standard Size, OR opening the
    // Customise popup before ever touching them (a customer may go
    // straight to "Need something different?" -- see the file header
    // and the task's own edge case). Both paths share this single
    // guard, so this can never fire twice regardless of order.
    function maybeFireEngaged() {
      if (engagedFired) return;
      if (!window.PTFunnelTracking) return;
      var accepted = window.PTFunnelTracking.fire('ProductEngaged', trackingParams(root, {
        selected_gender: root.dataset.soloGender || undefined
      }));
      if (accepted) engagedFired = true;
    }

    // Fires once Solo is genuinely ready -- Standard (Size) or
    // Customised (a saved fit draft). Never fires from merely
    // opening the popup: exploring it without saving never changes
    // getCustomisation(root), so isStateComplete() stays false on that
    // path alone.
    function maybeFireConfigured() {
      if (configuredFired) return;
      var state = getState(root);
      if (!isStateComplete(state)) return;
      if (!window.PTFunnelTracking) return;
      var accepted = window.PTFunnelTracking.fire('ProductConfigured', trackingParams(root, {
        selected_gender: root.dataset.soloGender || undefined,
        value: configuredValue(root, state)
      }));
      if (accepted) configuredFired = true;
    }

    function openCustomisationPopup(triggerEl) {
      if (!window.PTCustomisation) return;
      var gender = (root.getAttribute('data-solo-gender') || '').toUpperCase();
      window.PTCustomisation.open({
        gender: gender,
        initialDraft: getCustomisation(root),
        triggerEl: triggerEl,
        onSave: function (draft) {
          setCustomisation(root, draft);
          refresh(root);
          maybeFireConfigured();
        }
      });
    }

    // Standard Size -- untouched native association to the
    // product form; this listener only keeps this file's own DOM/
    // tracking state in sync, it never intercepts submission.
    root.addEventListener('change', function (evt) {
      if (evt.target.closest('[data-solo-size-select]')) {
        setCartError(root, null);
        refresh(root);
        maybeFireEngaged();
        maybeFireConfigured();
      }
    });

    root.addEventListener('click', function (evt) {
      // Same button/data-attribute serves both "open Customisation"
      // (not yet Customised) and "Edit Customisation" (already
      // Customised) -- both are a plain open() call with whatever
      // draft already exists; only the popup's own explicit save
      // (onSave, above) or an explicit "Use standard options instead"
      // click below ever changes this file's state.
      var customiseBtn = evt.target.closest('[data-solo-customise-cta]');
      if (customiseBtn) {
        maybeFireEngaged();
        openCustomisationPopup(customiseBtn);
        return;
      }

      var useStandardBtn = evt.target.closest('[data-solo-use-standard]');
      if (useStandardBtn) {
        setCustomisation(root, null);
        refresh(root);
        maybeFireConfigured();
        return;
      }
    });

    // Capture main and sticky submissions before the native handlers. This
    // also covers Enter/requestSubmit, which bypass button-click listeners.
    var form = getProductForm(root);
    var section = root.closest('.section-main-product');
    var forms = form ? [form] : [];
    if (section) forms = forms.concat(Array.from(section.querySelectorAll('sticky-atc form')));
    forms.forEach(function (submitForm) {
      submitForm.addEventListener('submit', function (evt) {
        var complete = isStateComplete(getState(root));
        if (!complete || submittingByRoot.get(root) || isCustomised(root)) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          if (!complete) {
            if (submitForm === form) form.reportValidity();
            else {
              setCartError(root, 'Choose a size');
              var size = root.querySelector('[data-solo-size-select]');
              if (size) {
                var header = document.querySelector('.pageheader--sticky');
                var offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
                window.scrollTo({ top: Math.max(0, window.scrollY + size.getBoundingClientRect().top - offset - 16), behavior: 'smooth' });
                size.focus({ preventScroll: true });
              }
            }
            return;
          }
          if (!form.reportValidity()) return;
          var button = getAddToCartButton(root);
          if (button && !button.disabled) submitCustomised(root, button);
        }
      }, true);
    });

    updateCustomiseCtaAvailability(root);

    // Run once on load: covers a browser restoring a previously
    // selected Size (bfcache / form-restore) before any change
    // event fires, and guarantees the Add to Cart button and the
    // pricing blocks all start in the correct state.
    refresh(root);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-solo-options]');
    for (var i = 0; i < roots.length; i++) {
      init(roots[i]);
    }
  }

  document.addEventListener('shopify:section:load', initAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
